import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { errorMessage, fieldError, http } from "../api/http";
import type { ChannelDTO, ConversationDTO, NotificationDTO } from "@justone/shared";
import { keys, useBootstrap, useChannels, useConversations, useMe, useNotifications, useStartConversation, useThreads, useUserMap, useUsers } from "../api/queries";
import { forgetAppPush, syncAppPush } from "../api/push";
import { isViewing, useRealtime } from "../api/realtime";
import { describeNotification, notificationPath } from "../lib/notice";
import { dmTitle } from "../lib/room";
import { useDrafts, useScheduled } from "../api/compose";
import { useFilters, useListMutations, useLists, useTaskSearch } from "../api/tasks";
import { today, VIEW_FILTERS } from "../lib/tasks";
import { clearAppBadge, useAppBadge } from "../lib/badge";
import { inNativeApp, iosInstallUrl, isIOS } from "../lib/native";
import { applyTheme } from "../lib/theme";
import { CommandPalette } from "../components/CommandPalette";
import { ConnectionBanner } from "../components/ConnectionBanner";
import { QuickAdd } from "../components/QuickAdd";
import { isMac, ShortcutsDialog, useKeyShortcut } from "../components/Shortcuts";
import { TooltipLayer } from "../components/Tooltip";
import { TaskPanelHost } from "../components/TaskPanel";
import { Avatar, Dialog, Field, Icon, useDismiss, useToast } from "../components/ui";
import { useMenuKeys } from "../components/Popover";
import { FilterDialog, NameDialog } from "./TaskPages";

/** 사이드바 열기(모바일)와 채널 만들기 대화상자를 하위 화면에서도 쓸 수 있게 */
export interface WorkspaceOutlet {
  openNav: () => void;
  openCreate: () => void;
  /** 화면이 바뀐 뒤에도 쓸 수 있는 이동 (토스트의 되돌리기 등: 그 화면은 이미 사라졌을 수 있다) */
  go: (to: string) => void;
}

/** 사이드바 맨 위 검색: 누르거나 Ctrl/⌘+K로 명령 팔레트를 연다 */
function SidebarSearch({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" className="sidebar-search" onClick={onOpen} aria-label="검색 및 이동" aria-keyshortcuts={isMac() ? "Meta+K" : "Control+K"}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span className="ss-label">검색·이동</span>
      <kbd aria-hidden="true">{isMac() ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}

const isQ = (e: KeyboardEvent) => e.key.toLowerCase() === "q";
const isHelp = (e: KeyboardEvent) => e.key === "?";

export function Workspace() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const [palette, setPalette] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  const openQuickAdd = useCallback(() => setQuickAdd(true), []);
  const openShortcuts = useCallback(() => setShortcuts(true), []);
  useKeyShortcut(isQ, openQuickAdd);
  useKeyShortcut(isHelp, openShortcuts);
  // Ctrl/⌘+K: 입력 중이어도 어디서나
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const paletteActions = useMemo(() => ({ quickAdd: openQuickAdd, createChannel: () => setCreateOpen(true), shortcuts: openShortcuts }), [openQuickAdd, openShortcuts]);
  useAppBadge();
  useUsers();
  const theme = useMe().preferences.theme;
  useEffect(() => applyTheme(theme), [theme]);
  useConversations();
  const channels = useChannels();

  const onRevoked = useCallback(() => {
    clearAppBadge();
    qc.clear();
    navigate("/login", { replace: true });
  }, [qc, navigate]);
  const toast = useToast();
  const users = useUserMap();
  // 앱을 보고 있을 때 온 알림: 그 메시지를 보고 있지 않으면 토스트로 알린다 (시스템 알림은 서비스 워커가 생략)
  const onNotify = useCallback(
    (n: NotificationDTO) => {
      const room = n.channelId !== null ? ({ kind: "channel", id: n.channelId } as const) : n.conversationId !== null ? ({ kind: "dm", id: n.conversationId } as const) : null;
      if (n.rootId === null && (n.type === "mention" || n.type === "thread_reply") && room && isViewing(room)) return;
      const who = n.actorId ? (users.get(n.actorId)?.displayName ?? "누군가") : "누군가";
      const place = n.channelId !== null ? `#${channels.data?.find((c) => c.id === n.channelId)?.name ?? "채널"}` : "DM";
      const path = notificationPath(n);
      toast(`${describeNotification(n, who, place)}: ${n.preview}`, path ? () => navigate(path) : undefined);
    },
    [users, toast, navigate, channels.data],
  );
  const { state: conn, reconnect } = useRealtime(onRevoked, onNotify);

  // JustOne 앱: 로그인하면 기기 토큰을 서버에 올리고, 알림을 누르면 앱이 이 함수로 그 화면을 연다 (문서를 다시 받지 않고)
  const appPush = useBootstrap().data?.appPush ?? false;
  useEffect(() => {
    if (inNativeApp() && appPush) void syncAppPush().catch(() => {});
  }, [appPush]);
  useEffect(() => {
    if (!inNativeApp()) return;
    const w = window as { __justoneOpen?: (path: string) => void };
    w.__justoneOpen = (path) => navigate(path);
    return () => {
      delete w.__justoneOpen;
    };
  }, [navigate]);

  // 화면을 옮기면 모바일 사이드바를 닫는다
  useEffect(() => setNavOpen(false), [location.pathname]);

  if (channels.isError) return <div className="splash">채널 목록을 불러오지 못했습니다. {errorMessage(channels.error)}</div>;

  const outlet: WorkspaceOutlet = { openNav: () => setNavOpen(true), openCreate: () => setCreateOpen(true), go: (to) => navigate(to) };
  return (
    <div className={`workspace${navOpen ? " nav-open" : ""}`}>
      <a
        href="#main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        본문으로 건너뛰기
      </a>
      <Sidebar channels={channels.data ?? []} onCreate={() => setCreateOpen(true)} onQuickAdd={openQuickAdd} onSearch={() => setPalette(true)} />
      <div className="scrim" onClick={() => setNavOpen(false)} />
      <div className="main" id="main-content" tabIndex={-1}>
        <ConnectionBanner state={conn} reconnect={reconnect} />
        {channels.data ? <Outlet context={outlet} /> : <div className="splash">불러오는 중…</div>}
        {channels.data && <TaskPanelHost />}
      </div>
      <CreateChannelDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <QuickAddDialog open={quickAdd} onClose={() => setQuickAdd(false)} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} actions={paletteActions} />
      <ShortcutsDialog open={shortcuts} onClose={() => setShortcuts(false)} />
      <TooltipLayer />
    </div>
  );
}

/** 지금 보는 채널이나 목록을 기본값으로 */
function QuickAddDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const channelId = /^\/c\/(\d+)/.exec(location.pathname)?.[1];
  const listId = /^\/lists\/(\d+)/.exec(location.pathname)?.[1];
  const onToday = location.pathname === "/today";
  return (
    <Dialog open={open} onClose={onClose} label="할일 추가">
      <div className="dialog-body">
      <h2>할일 추가</h2>
      <QuickAdd
        defaults={channelId ? { channelId: Number(channelId) } : listId ? { listId: Number(listId) } : onToday ? { dueDate: today() } : {}}
        allowPersonal={!!channelId}
        autoFocus
        onCancel={onClose}
        onDone={onClose}
      />
      <p className="qa-legend">
        <span className="tk-date">날짜</span> 내일, 금요일, 다음주 월요일, 10/20 · <span className="tk-time">시각</span> 오후 3시, 저녁 · <span className="tk-recur">반복</span> 매주 월요일, 매달 1일 · <span className="tk-target">#목록·채널</span> · <span className="tk-assignee">@담당자</span> · <span className="tk-label">+라벨</span> · <span className="tk-priority">우선순위</span> !1 높음, !2 중간, !3 낮음
      </p>
      </div>
    </Dialog>
  );
}

function TaskLinks() {
  const today = useTaskSearch(VIEW_FILTERS.today);
  const inbox = useTaskSearch(VIEW_FILTERS.inbox);
  const count = (n: number | undefined, label: string) =>
    n ? (
      <span className="count-plain" aria-label={`${label} ${n}개`}>
        {n > 99 ? "99+" : n}
      </span>
    ) : null;
  const link = (to: string, name: string, icon: string, n?: number) => (
    <NavLink to={to} className={({ isActive }) => ["channel-link", isActive && "active"].filter(Boolean).join(" ")}>
      <span className="hash" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d={icon} />
        </svg>
      </span>
      <span className="name">{name}</span>
      {count(n, name)}
    </NavLink>
  );
  return (
    <>
      {link("/inbox", "받은함", "M4 13h4l2 3h4l2-3h4M4 13l2-8h12l2 8v6H4v-6Z", inbox.data?.length)}
      {link("/today", "오늘", "M5 5h14v15H5zM5 10h14M9 3v4M15 3v4", today.data?.length)}
      {link("/upcoming", "다가오는", "M4 6h16M4 12h10M4 18h6")}
      {link("/labels", "라벨", "M3 12V4h8l10 10-8 8L3 12ZM7.5 7.5h.01")}
      {link("/wiki", "공통 위키", "M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5ZM4 19a2 2 0 0 1 2-2h13M8 7h7")}
    </>
  );
}

function ListsSection() {
  const { data: lists = [] } = useLists();
  const { data: filters = [] } = useFilters();
  const ops = useListMutations();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [filtering, setFiltering] = useState(false);
  return (
    <>
      <div className="sidebar-section">
        <span id="lists-heading">내 목록</span>
        <button type="button" className="icon-btn" onClick={() => setCreating(true)} aria-label="목록 만들기">
          <Icon name="plus" />
        </button>
      </div>
      <ul aria-labelledby="lists-heading" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {lists.map((l) => (
          <li key={l.id}>
            <NavLink to={`/lists/${l.id}`} className={({ isActive }) => ["channel-link", isActive && "active"].filter(Boolean).join(" ")}>
              <span className="hash" aria-hidden="true">
                ≡
              </span>
              <span className="name">{l.name}</span>
              {l.openCount > 0 && <span className="count-plain">{l.openCount}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="sidebar-section">
        <span id="filters-heading">필터</span>
        <button type="button" className="icon-btn" onClick={() => setFiltering(true)} aria-label="필터 만들기">
          <Icon name="plus" />
        </button>
      </div>
      <ul aria-labelledby="filters-heading" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {filters.map((f) => (
          <li key={f.id}>
            <NavLink to={`/filters/${f.id}`} className={({ isActive }) => ["channel-link", isActive && "active"].filter(Boolean).join(" ")}>
              <span className="hash" aria-hidden="true">
                ⌕
              </span>
              <span className="name">{f.name}</span>
            </NavLink>
          </li>
        ))}
      </ul>
      {creating && (
        <NameDialog
          title="목록 만들기"
          hint="예: 장보기, 공부. 공백 없이. 빠른 입력에서 #이름으로 이 목록에 넣습니다."
          onClose={() => setCreating(false)}
          onSave={async (name) => navigate(`/lists/${(await ops.create.mutateAsync(name)).id}`)}
        />
      )}
      {filtering && <FilterDialog onClose={() => setFiltering(false)} />}
    </>
  );
}

function Sidebar({ channels, onCreate, onQuickAdd, onSearch }: { channels: ChannelDTO[]; onCreate: () => void; onQuickAdd: () => void; onSearch: () => void }) {
  const joined = channels.filter((c) => c.joined);
  const totalUnread = joined.reduce((n, c) => n + c.unread, 0);

  useEffect(() => {
    document.title = totalUnread > 0 ? `(${totalUnread}) JustOne` : "JustOne";
  }, [totalUnread]);

  return (
    <nav className="sidebar" aria-label="채널">
      <div className="sidebar-head">
        <Link to="/" className="wordmark" style={{ textDecoration: "none" }}>
          <img src="/icons/favicon.svg" alt="" />
          JustOne
        </Link>
        <span className="head-actions">
          <button type="button" className="icon-btn" onClick={onQuickAdd} aria-label="할일 추가 (Q)" aria-keyshortcuts="Q" data-tip="할일 추가" data-kbd="Q">
            <Icon name="plus" />
          </button>
          <BellLink />
        </span>
      </div>
      <SidebarSearch onOpen={onSearch} />
      <div className="sidebar-scroll">
        <TaskLinks />
        <ThreadsLink />
        <DraftsLink />
        <div className="sidebar-section">
          <span id="channels-heading">채널</span>
          <button type="button" className="icon-btn" onClick={onCreate} aria-label="채널 만들기">
            <Icon name="plus" />
          </button>
        </div>
        <ul aria-labelledby="channels-heading" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {joined.map((c) => (
            <li key={c.id}>
              <NavLink to={`/c/${c.id}`} className={({ isActive }) => ["channel-link", isActive && "active", c.unread > 0 && "unread", c.archived && "archived"].filter(Boolean).join(" ")}>
                <span className="hash" aria-hidden="true">
                  #
                </span>
                <span className="name">{c.name}</span>
                {c.unread > 0 && (
                  <span className="badge" aria-label={`안 읽은 메시지 ${c.unread}개`}>
                    {c.unread > 99 ? "99+" : c.unread}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
        <DirectMessages />
        <ListsSection />
        <NavLink to="/channels" className="sidebar-link" style={{ marginTop: 6 }}>
          <span className="hash" aria-hidden="true" style={{ width: 14, display: "inline-grid", placeItems: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </span>
          채널 둘러보기
        </NavLink>
      </div>
      <MeMenu />
    </nav>
  );
}

function BellLink() {
  const { data } = useNotifications();
  const unread = data?.unread ?? 0;
  return (
    <NavLink to="/notifications" className={({ isActive }) => `icon-btn bell${isActive ? " active" : ""}`} aria-label={unread ? `알림 (안 읽은 알림 ${unread}개)` : "알림"} data-tip="알림">
      <Icon name="bell" />
      {unread > 0 && <span className="badge bell-badge">{unread > 99 ? "99+" : unread}</span>}
    </NavLink>
  );
}

function DirectMessages() {
  const me = useMe();
  const users = useUserMap();
  const { data: conversations = [] } = useConversations();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="sidebar-section">
        <span id="dm-heading">다이렉트 메시지</span>
        <button type="button" className="icon-btn" onClick={() => setOpen(true)} aria-label="새 DM">
          <Icon name="plus" />
        </button>
      </div>
      <ul aria-labelledby="dm-heading" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {conversations.map((c: ConversationDTO) => {
          const others = c.memberIds.filter((id) => id !== me.id);
          const single = others.length === 1 ? users.get(others[0]!) : undefined;
          return (
            <li key={c.id}>
              <NavLink to={`/d/${c.id}`} className={({ isActive }) => ["channel-link", "dm-link", isActive && "active", c.unread > 0 && "unread"].filter(Boolean).join(" ")}>
                {single ? (
                  <Avatar user={single} size={20} presence />
                ) : (
                  <span className="group-mark" aria-hidden="true">
                    {others.length}
                  </span>
                )}
                <span className="name">{dmTitle(c, users, me.id)}</span>
                {c.unread > 0 && (
                  <span className="badge" aria-label={`안 읽은 메시지 ${c.unread}개`}>
                    {c.unread > 99 ? "99+" : c.unread}
                  </span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
      <NewDMDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function NewDMDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const { data: users = [] } = useUsers();
  const start = useStartConversation();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const candidates = users.filter((u) => u.active && u.id !== me.id && (!query || u.displayName.includes(query) || u.username?.includes(query.toLowerCase())));

  const close = () => {
    setPicked([]);
    setQuery("");
    setErr(null);
    onClose();
  };
  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 7 ? p : [...p, id]));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    start.mutate(picked, {
      onSuccess: (c) => {
        close();
        navigate(`/d/${c.id}`);
      },
      onError: setErr,
    });
  };

  return (
    <Dialog open={open} onClose={close} label="새 DM">
      <form onSubmit={submit}>
        <h2>새 DM</h2>
        <p style={{ marginTop: 0, color: "var(--ink-2)", fontSize: 14 }}>DM 메시지는 7일 후 사라집니다. 남겨야 할 이야기는 채널에서 하세요.</p>
        {err ? <p className="form-error">{errorMessage(err)}</p> : null}
        <Field id="dm-search" label="이름 또는 아이디로 찾기" hint={`나를 포함해 8명까지 (${picked.length}/7명 선택)`}>
          <input id="dm-search" className="input" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" autoFocus />
        </Field>
        <ul className="pick-list">
          {candidates.map((u) => (
            <li key={u.id}>
              <label>
                <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} disabled={!picked.includes(u.id) && picked.length >= 7} />
                <Avatar user={u} size={24} presence />
                <span>{u.displayName}</span>
                {u.username && <span className="handle">@{u.username}</span>}
              </label>
            </li>
          ))}
          {candidates.length === 0 && <li className="empty">찾는 사람이 없습니다.</li>}
        </ul>
        <div className="buttons">
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={picked.length === 0 || start.isPending}>
            대화 시작
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ThreadsLink() {
  const { data = [] } = useThreads();
  const unread = data.reduce((n, t) => n + (t.unread > 0 ? 1 : 0), 0);
  return (
    <NavLink to="/threads" className={({ isActive }) => ["channel-link", isActive && "active", unread > 0 && "unread"].filter(Boolean).join(" ")}>
      <span className="hash" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 5h16v10H8l-4 4V5Z" />
        </svg>
      </span>
      <span className="name">스레드</span>
      {unread > 0 && (
        <span className="badge" aria-label={`새 답글이 있는 스레드 ${unread}개`}>
          {unread}
        </span>
      )}
    </NavLink>
  );
}

/** 초안: 보내지 않은 글과 예약한 메시지. 수는 회색으로 (안 읽음이 아니다) */
function DraftsLink() {
  const { data: drafts = [] } = useDrafts();
  const { data: scheduled = [] } = useScheduled();
  const failed = scheduled.some((s) => s.status === "failed");
  const count = drafts.length + scheduled.length;
  return (
    <NavLink to="/drafts" className={({ isActive }) => ["channel-link", isActive && "active"].filter(Boolean).join(" ")}>
      <span className="hash" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20h4L19 9l-4-4L4 16v4ZM13.5 6.5l4 4" />
        </svg>
      </span>
      <span className="name">초안</span>
      {count > 0 && (
        <span className={`count-plain${failed ? " failed" : ""}`} aria-label={failed ? "보내지 못한 예약 메시지가 있습니다" : `초안·예약 ${count}개`}>
          {count}
        </span>
      )}
    </NavLink>
  );
}

function MeMenu() {
  const me = useMe();
  const users = useUserMap();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss<HTMLDivElement>(open, close);
  useMenuKeys(open, ref);

  const iosApp = useBootstrap().data?.iosApp ?? null;
  const [install, setInstall] = useState(false);

  const logout = async () => {
    await forgetAppPush().catch(() => {});
    await http.post("/api/auth/logout").catch(() => {});
    clearAppBadge();
    qc.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="sidebar-foot" ref={ref}>
      {open && (
        <div className="menu" role="menu">
          <Link to="/settings" role="menuitem" onClick={close}>
            내 계정
          </Link>
          {iosApp && isIOS() && !inNativeApp() && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                setInstall(true);
              }}
            >
              iPhone·iPad 앱 설치
            </button>
          )}
          {me.role === "admin" && (
            <>
              <Link to="/admin/members" role="menuitem" onClick={close}>
                멤버와 초대
              </Link>
              <Link to="/admin/wiki-templates" role="menuitem" onClick={close}>
                위키 템플릿
              </Link>
              <Link to="/admin/wiki-purge" role="menuitem" onClick={close}>
                위키 이력 영구 제거
              </Link>
            </>
          )}
          <button type="button" role="menuitem" onClick={logout}>
            로그아웃
          </button>
        </div>
      )}
      <button type="button" className="me-button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        <Avatar user={users.get(me.id) ?? me} size={32} presence />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", color: "var(--pine-strong)", fontWeight: 600 }}>{me.displayName}</span>
          <span style={{ display: "block", fontSize: 12, color: "var(--pine-muted)" }}>{me.role === "admin" ? "관리자" : "멤버"}</span>
        </span>
      </button>
      <InstallAppDialog open={install} onClose={() => setInstall(false)} />
    </div>
  );
}

/** 링크로 앱 설치: iPhone·iPad가 서버의 설치 정보(/ios/manifest.plist)를 받아 묻고 설치한다 */
function InstallAppDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} label="JustOne 앱 설치">
      <div className="dialog-body">
        <h2>JustOne 앱 설치</h2>
        <p>
          <strong>설치</strong>를 누르고, 뜨는 창에서 한 번 더 <strong>설치</strong>를 누르세요. 홈 화면에 JustOne 앱이 생깁니다.
        </p>
        <p className="muted">관리자가 등록한 기기에만 설치됩니다. 설치할 수 없다고 나오면 관리자에게 이 기기를 등록해 달라고 하세요. 알림은 앱의 내 계정 → 알림에서 다시 켭니다.</p>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            닫기
          </button>
          <a className="btn btn-primary" href={iosInstallUrl()} onClick={onClose}>
            설치
          </a>
        </div>
      </div>
    </Dialog>
  );
}

function CreateChannelDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setName("");
    setTopic("");
    setErr(null);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ch = await http.post<{ id: number }>("/api/channels", { name, topic });
      await qc.invalidateQueries({ queryKey: keys.channels });
      close();
      navigate(`/c/${ch.id}`);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} label="채널 만들기">
      <form onSubmit={submit}>
        <h2>채널 만들기</h2>
        {err && !fieldError(err, "name") ? <p className="form-error">{errorMessage(err)}</p> : null}
        <Field id="channel-name" label="이름" hint="공백은 -로 바뀝니다. 예: 집안일, 영업-2팀" error={fieldError(err, "name")}>
          <input id="channel-name" className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
        </Field>
        <Field id="channel-topic" label="설명 (선택)" error={fieldError(err, "topic")}>
          <input id="channel-topic" className="input" maxLength={200} value={topic} onChange={(e) => setTopic(e.target.value)} autoComplete="off" />
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            만들기
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** "/" → 기본 채널(없으면 첫 참여 채널) */
export function HomeRedirect() {
  const channels = useChannels();
  const list = channels.data ?? [];
  const target = list.find((c) => c.joined && c.isDefault) ?? list.find((c) => c.joined) ?? list[0];
  if (!channels.data) return null;
  return target ? <Navigate to={`/c/${target.id}`} replace /> : <Navigate to="/channels" replace />;
}
